import { useEffect, useState } from 'react';

import { Boundary, getBoundariesAtLocation } from '../../../api/fishrules/getBoundariesAtLocation';
import { ICoordinates } from '../../../interfaces/geo';
import { reportMykissClientError } from '../../errorHandling/client';
import { useHandleUnmount } from '../useHandleUnmount';

export interface IBoundary {
  name: string;
  id: number;
  regulationNotes: string;
  prohibited: boolean;
  prohibitedMessage?: string;
  rank: number;
}

interface Result {
  isLoading: boolean;
  name?: string;
  boundaries?: IBoundary[];
}

function getBoundaryName(boundaries: Boundary[]): string | undefined {
  if (boundaries.length === 0) {
    return undefined;
  }

  // eslint-disable-next-line no-magic-numbers
  const boundariesWithoutRank10 = boundaries.filter(({ rank }) => rank !== 10);

  return boundariesWithoutRank10.length ? boundariesWithoutRank10[0].name : boundaries[0].name;
}

export function useRegulationBoundaryAtLocation(coords: ICoordinates | undefined): Result {
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState<string>();
  const [boundaries, setBoundaries] = useState<IBoundary[]>();

  const latitude = coords?.latitude;
  const longitude = coords?.longitude;
  const unmounted = useHandleUnmount();

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);

    if (latitude !== undefined && longitude !== undefined) {
      getBoundariesAtLocation({ latitude, longitude }, controller.signal)
        .then(({ results, success }) => {
          if (unmounted.current) {
            return;
          }
          if (success && results.transformedBoundaries.length) {
            setName(getBoundaryName(results.transformedBoundaries));
          }
          setBoundaries(
            results.transformedBoundaries.map(b => ({
              name: b.name,
              id: b.id,
              regulationNotes: b.regulation_notes,
              prohibited: b.prohibited === 1,
              prohibitedMessage: b.prohibited_message,
              rank: b.rank,
            })),
          );
          setIsLoading(false);
        })
        .catch(error => {
          if (error.name !== 'AbortError') {
            reportMykissClientError(error);
          }
          if (!unmounted.current) {
            setIsLoading(false);
          }
        });
    }

    return () => {
      controller.abort();
    };
  }, [latitude, longitude, unmounted]);

  return { isLoading, name, boundaries };
}
